import { useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import DateRangePicker from "../DateRangePicker";
import { Filter } from "lucide-react";
import { SideDrawer } from "../../../component/SideDrawer/SideDrawer"; // adjust path

export default function MyLineChart({ setStartDate, setEndDate, inDirect, direct }) {
    // group indirect
    const groupedInDirect = inDirect.reduce((acc, item) => {
        const date = new Date(item.createdAt).toISOString().split("T")[0];
        const total = parseFloat(item.totalAmount || item.total) || 0;

        if (!acc[date]) acc[date] = 0;
        acc[date] += total;
        return acc;
    }, {});

    // group direct
    const groupedDirect = direct.reduce((acc, item) => {
        const date = new Date(item.createdAt).toISOString().split("T")[0];
        const total = parseFloat(item.totalAmount || item.total) || 0;

        if (!acc[date]) acc[date] = 0;
        acc[date] += total;
        return acc;
    }, {});

    // merge by date
    const allDates = new Set([...Object.keys(groupedInDirect), ...Object.keys(groupedDirect)]);
    const mergedData = Array.from(allDates)
        .map((date) => ({
            name: date,
            Direct: groupedDirect[date] || 0,
            Indirect: groupedInDirect[date] || 0,
        }))
        .sort((a, b) => new Date(a.name) - new Date(b.name));

    const finalData = mergedData; // last 7 days (optional)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    return (
        <div style={{ width: "100%", height: 320 }} className="bg-white p-5">
            <div className="flex flex-row justify-between items-center">
                <h5 className="md:text-[20px] text-[20px] font-bold text-gray-800 mb-4">
                    Days wise Direct vs Indirect Purchase Orders
                </h5>
                <Filter
                    className="cursor-pointer"
                    onClick={() => setIsDrawerOpen(true)}
                />
            </div>

            <ResponsiveContainer>
                <LineChart
                    data={finalData}
                    margin={{ top: 20, right: 10, left: 0, bottom: 40 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis
                        tickFormatter={(value) => {
                            if (value >= 1000000) return (value / 1000000).toFixed(1) + "M";
                            if (value >= 1000) return (value / 1000).toFixed(1) + "k";
                            return value;
                        }}
                    />
                    <Tooltip
                        formatter={(value) =>
                            new Intl.NumberFormat("en-IN", {
                                style: "currency",
                                currency: "INR",
                                maximumFractionDigits: 0,
                            }).format(value)
                        }
                    />
                    <Legend
                        content={({ payload }) => (
                            <div className="flex flex-wrap justify-center items-center mt-2 w-full px-2 bg-white md:mb-0 mb-10">
                                {payload.map((entry, index) => (
                                    <div
                                        key={`item-${index}`}
                                        className="flex items-center mx-2 my-1 text-xs sm:text-sm"
                                    >
                                        <span
                                            className="w-3 h-3 inline-block mr-2 rounded"
                                            style={{ backgroundColor: entry.color }}
                                        />
                                        <span className="truncate max-w-[80px] sm:max-w-none">
                                            {entry.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                    {/* two lines */}
                    <Line type="monotone" dataKey="Direct" stroke="#82ca9d" activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="Indirect" stroke="#8884d8" />
                </LineChart>
            </ResponsiveContainer>

            <SideDrawer
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                title="Filter Options"
                customWidth
            >
                <div className="px-12">
                    <DateRangePicker
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        setIsDrawerOpen={setIsDrawerOpen}
                    />
                </div>
            </SideDrawer>
        </div>
    );
}
